import { Injectable } from '@angular/core';
import { BlobService } from './blob.service';

export interface CsvHeader<T> {
  text: string;
  order: number | null | undefined;
  valueMap: (v: T) => string | number | boolean | null | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class CsvService {
  constructor(private readonly blobService: BlobService) {}

  createAndDownload<T>(headers: CsvHeader<T>[], data: T[], fileName: string): void {
    if (!headers || headers.length === 0) {
      throw new Error('Headers required to create a CSV.');
    }

    if (!fileName) {
      throw new Error('No file name provided for the CSV.');
    }

    const orderedHeaders = this.orderHeaders(headers);
    const csvString = this.createCsv(orderedHeaders, data);

    this.download(csvString, fileName);
  }

  private orderHeaders<T>(headers: CsvHeader<T>[]): readonly CsvHeader<T>[] {
    const orderedHeaders = headers.sort((a, b) => {
      if (!a?.order || !b?.order) {
        return 0;
      }
      if (a.order < b.order) {
        return -1;
      }
      if (a.order > b.order) {
        return 1;
      }
      return 0;
    });

    return orderedHeaders;
  }

  private download(csvString: string, fileName: string): void {
    if (!fileName.endsWith('.csv')) {
      fileName += '.csv';
    }

    const blob = new Blob([csvString], { type: 'text/csv' });

    this.blobService.downloadBlobToClient(blob, fileName);
  }

  private createCsv<T>(headers: readonly CsvHeader<T>[], data: T[]): string {
    let csvString = this.createRow(headers, (h) => h.text);

    data.forEach((d) => {
      csvString += this.createRow(headers, (h) => h.valueMap(d));
    });

    return csvString;
  }

  private createRow<T>(
    headers: readonly CsvHeader<T>[],
    valueMap: (h: CsvHeader<T>) => string | number | boolean | null | undefined
  ): string {
    let row = '';

    headers.forEach((h) => {
      const value = valueMap(h);
      const stringifiedValue: string = value !== null && value !== undefined ? JSON.stringify(value).trim() : '';

      row += `${stringifiedValue},`;
    });

    row = row.slice(0, row.length - 1); // remove the trailing comma
    row += '\r\n';

    return row;
  }
}
